import { ArrowPathIcon, ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import { Balance, Text, toolkitCx } from "@sundae/ui-toolkit";
import { useCallback, useMemo, useState } from "react";

import { useI18N } from "@sundae/react-hooks";
import { ITextProps } from "@sundae/ui-toolkit/dist/types/components/atoms/Text/Text";
import { IButtonGroupProps } from "@sundae/ui-toolkit/dist/types/components/molecules/ButtonGroup/ButtonGroup";
import { useWalletDetails } from "@sundae/wallet-react";
import { AssetAmount } from "@sundaeswap/asset";
import cx from "classnames";
import CardanoExplorer from "../../components/CardanoExplorer";
import { ADA_METADATA, CARDANO_EXPLORER_LINK_MAP } from "../../constants/cardano.constants";
import { useTasteTestHistory } from "../../gql/hooks/taste-test.query";
import { useTasteTestContext } from "../../pages/TasteTestDetail/context";
import { useProfileAppContext } from "../../stores";
import { useCardanoExplorer } from "../useCardanoExplorer";
const {
  theme: { colors },
} = require("@sundae/tailwind-config");

const defaultPrimaryColor = colors.primary["DEFAULT"];

export const useTasteTestPendingOrderView = () => {
  const { currentLocale, t } = useI18N("taste-test");
  const [refreshedTimestamp, setRefreshedTimestamp] = useState<number>();
  const [isRefreshDisabled, setIsRefreshDisabled] = useState(false);
  const { details: walletDetails } = useWalletDetails();
  const {
    state: { cardanoExplorer, privacyEnabled },
  } = useProfileAppContext();
  const {
    derived: { hasContribution },
    state: { actionType, persistedContributionsHistory, tasteTest, tasteTestPersonalization },
  } = useTasteTestContext();
  const { refetch: refetchTasteTestHistory } = useTasteTestHistory(
    walletDetails.stakingAddress,
    tasteTest?.policyId,
    1000 * 5 /** 5 Seconds */
  );
  const currentPendingContribution = persistedContributionsHistory?.[0];
  const isClaim = currentPendingContribution?.type === "claim";

  const handleRefreshButton = useCallback(() => {
    const now = Date.now();
    refetchTasteTestHistory();
    setIsRefreshDisabled(true);
    setRefreshedTimestamp(now);
    setTimeout(() => {
      setIsRefreshDisabled(false);
    }, 1000 * 5 /** 10 Seconds */);
  }, [refetchTasteTestHistory, setRefreshedTimestamp, setIsRefreshDisabled]);

  const explorerLink = useCardanoExplorer({
    type: CARDANO_EXPLORER_LINK_MAP.transaction[cardanoExplorer],
    hash: currentPendingContribution?.txHash ?? "",
  });

  const variant: ITextProps["variant"] =
    currentPendingContribution?.type === "withdraw" ? "error" : "success";

  const orderTypeLabel = useMemo(() => {
    if (currentPendingContribution?.type === "withdraw" && actionType === "withdraw") {
      return t("detail.widget.pendingOrder.amount", { context: "withdraw" });
    }
    if (
      (hasContribution &&
        actionType === "update" &&
        currentPendingContribution?.type === "deposit") ||
      currentPendingContribution?.type === "update"
    ) {
      return t("detail.widget.pendingOrder.amount", { context: "update" });
    }

    if (isClaim) {
      return t("detail.widget.pendingOrder.amount", { context: "claim" });
    }

    return t("detail.widget.pendingOrder.amount", { context: "deposit" });
  }, [actionType, currentPendingContribution?.type, isClaim, t, hasContribution]);

  const memoizedAmount = useMemo(() => {
    return new AssetAmount(currentPendingContribution?.details?.amount, isClaim ? 0 : ADA_METADATA);
  }, [currentPendingContribution?.details?.amount, currentPendingContribution?.type, isClaim]);

  const transactionItems = useMemo(() => {
    return [
      {
        id: 0,
        label: t("details.txId", { ns: "orders" }),
        value: (
          <CardanoExplorer
            hash={currentPendingContribution?.txHash ?? ""}
            style={{
              color: tasteTestPersonalization?.primaryColor ?? defaultPrimaryColor,
            }}
            type={CARDANO_EXPLORER_LINK_MAP.transaction[cardanoExplorer]}
            truncateFrom={5}
            truncateUntil={4}
          />
        ),
      },
      {
        id: 1,
        label: t("orderTypeSelect.title", { ns: "exchange" }),
        value: (
          <Text className="capitalize" tag="span" variant={variant}>
            {currentPendingContribution?.type}
          </Text>
        ),
      },
      {
        id: 2,
        label: orderTypeLabel,
        value: (
          <div className="flex items-center gap-1">
            <Balance
              abbreviate={isClaim}
              formatOpts={{
                minimumFractionDigits: 0,
                maximumFractionDigits: ADA_METADATA.decimals,
              }}
              locale={currentLocale}
              privacy={privacyEnabled}
              showSymbol={!isClaim}
              value={memoizedAmount}
            />

            {isClaim && (
              <Text tag="span" size="xs">
                {t("lpToken", { ns: "glossary", context: "short" })}
              </Text>
            )}
          </div>
        ),
      },
    ];
  }, [
    cardanoExplorer,
    currentLocale,
    currentPendingContribution,
    isClaim,
    orderTypeLabel,
    privacyEnabled,
    t,
    tasteTestPersonalization?.primaryColor,
  ]);

  const buttonGroupConfig: IButtonGroupProps = useMemo(
    () => ({
      className: "w-full",
      widthRatio: ["1/2", "1/2"],
      size: "md",
      withBackdropBlur: true,
      primaryButtonConfig: {
        buttonProps: {
          className: "hover:-translate-y-[2px] group",
          disabled: false,
          onClick: () => window.open(explorerLink),
          style: { backgroundColor: tasteTestPersonalization?.primaryColor || "" },
        },
        label: t("detail.widget.pendingOrder.button", { context: "view" }),
        icon: ArrowTopRightOnSquareIcon,
        iconProps: {
          className: cx("group-hover:scale-110", toolkitCx.layout.transition.transform),
        },
      },
      secondaryButtonConfig: {
        buttonProps: {
          className: "hover:-translate-y-[2px] group",
          disabled: isRefreshDisabled,
          onClick: handleRefreshButton,
        },
        icon: ArrowPathIcon,
        iconProps: {
          className: cx("group-hover:scale-110", toolkitCx.layout.transition.transform, {
            "animate-spin": isRefreshDisabled,
          }),
        },
        label: t("detail.widget.pendingOrder.button", {
          context: isRefreshDisabled ? "refreshing" : "refresh",
        }),
      },
    }),
    [explorerLink, handleRefreshButton, isRefreshDisabled, t]
  );

  return {
    buttonGroupConfig,
    isRefreshDisabled,
    refreshedTimestamp,
    transactionItems,
  };
};
